import React, { useCallback, useState } from 'react';
import { MetricDisplayProps, ColorStatus } from '../types';
import { CircleGauge } from './charts/CircleGauge';
import { DataCard } from './charts/DataCard';
import { Tooltip } from './Tooltip';
import { Icon, isValidIcon } from './Icon';
import { ChunkBoundary } from './ChunkBoundary';
import { resolveCardStatusLabel } from '../utils/statusColors';
import './MetricCard.css';

// Recharts is the largest removable dependency in the resident bundle, so every
// chart that imports it is lazy — this keeps recharts out of the resident's
// initial download and lets webpack hoist it into a shared async chunk.
// CircleGauge/DataCard above don't use recharts and stay static so the most
// common card types (gauges, plain values) render instantly.
//
// The factories are recreated (not just referenced) on chunk-load retry: see
// makeLazyCharts() below. React.lazy() caches a rejected import forever on the
// lazy component reference itself, so a module-scope `React.lazy(() => import(...))`
// could never be retried in the same session — only constructing a fresh
// React.lazy() actually re-triggers the dynamic import.
function makeLazyCharts() {
  return {
    HorizontalBarChart: React.lazy(() => import('./charts/HorizontalBarChart')),
    VerticalBarChart: React.lazy(() => import('./charts/VerticalBarChart')),
    TrendLineChart: React.lazy(() => import('./charts/TrendLineChart')),
    SingleValueLineChart: React.lazy(() => import('./charts/SingleValueLineChart')),
    HorizontalTimeSeriesBar: React.lazy(() => import('./charts/HorizontalTimeSeriesBar')),
    VerticalTimeSeriesBar: React.lazy(() => import('./charts/VerticalTimeSeriesBar')),
  };
}

export interface ThresholdRanges {
  greenMin?: number;
  greenMax?: number;
  yellowMin?: number;
  yellowMax?: number;
  redMin?: number;
  redMax?: number;
}

interface ExtendedMetricDisplayProps extends MetricDisplayProps {
  zscore?: number;
  icon?: string;
  metadata?: any;
  statusLabel?: string;
  /** e.g. "Compared with men aged 65-69" - shown under the value so the color is explained. */
  normativeBasis?: string;
  tooltipText?: string;
  timeSeriesData?: Array<{ date: string; value: number; }>;
  dateRangeLabel?: string;
  // Highest configured threshold bound (greenMax, yellowMax, redMax) — used to cap chart axes
  configRangeMax?: number;
  /** When true, renders help text as a persistent note below the chart instead of a hover tooltip */
  showInlineNote?: boolean;
  thresholds?: ThresholdRanges;
  /** Shown when chart data is from a fallback range, not the selected period */
  timeSeriesFallbackNotice?: string;
  /** When set, disables threshold/status color coding and renders charts in this single color */
  customColor?: string;
  /**
   * True when `value` and the time-series points have already been multiplied by
   * 100 for display. CareDaily metadata (min/max) is always in the raw API scale,
   * so it must be scaled to match before being used as a chart max.
   */
  displayAs0to100?: boolean;
  /** Per-bar color for time-series bars when norms drive the card. See deriveMetricCard. */
  pointColorStatus?: (value: number) => ColorStatus | null;
}

export const MetricCard: React.FC<ExtendedMetricDisplayProps> = ({
  title,
  value,
  targetValue,
  targetMin,
  targetMax,
  colorStatus,
  units,
  visualizationType,
  zscore,
  icon,
  metadata,
  statusLabel,
  normativeBasis,
  tooltipText,
  timeSeriesData,
  dateRangeLabel,
  configRangeMax,
  showInlineNote = false,
  thresholds,
  timeSeriesFallbackNotice,
  customColor,
  displayAs0to100 = false,
  pointColorStatus
}) => {
  // Owned in state (not module scope) so a chunk-load retry can swap in fresh
  // React.lazy() references — see makeLazyCharts() above for why.
  const [lazyCharts, setLazyCharts] = useState(() => makeLazyCharts());
  const {
    HorizontalBarChart,
    VerticalBarChart,
    TrendLineChart,
    SingleValueLineChart,
    HorizontalTimeSeriesBar,
    VerticalTimeSeriesBar
  } = lazyCharts;
  const handleChartRetry = useCallback(() => setLazyCharts(makeLazyCharts()), []);

  const isEmojiIcon = (name: string) => name.length <= 2 && !/^[a-zA-Z0-9]+$/.test(name);

  // Color is never the only signal for status (WCAG 1.4.1). In single-color mode no
  // fallback word is derived from the switched-off thresholds; see resolveCardStatusLabel.
  const resolvedStatusLabel = resolveCardStatusLabel(statusLabel, colorStatus, customColor);

  // Residents read any arrow or trend line as "your score changed", so both are gated
  // on the series actually moving: at least two readings, and not all the same value.
  // This is the only reliable gate, because the CareDaily z-score measures distance
  // from the resident's rolling average — not change over time — and stays non-zero on
  // a flat series (pilot feedback, Moorings Park, Sept 2026).
  const hasSeriesVariation = (series?: Array<{ date: string; value: number }>): boolean =>
    !!series && series.length >= 2 && series.some(d => d.value !== series[0].value);

  const seriesIsFlat = !!timeSeriesData && timeSeriesData.length >= 2
    && !hasSeriesVariation(timeSeriesData);

  const renderVisualization = () => {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    const numericValue = (typeof parsed === 'number' && !isNaN(parsed)) ? parsed : undefined;
    const fallbackCard = (
      <DataCard
        value={value}
        colorStatus={colorStatus}
        customColor={customColor}
        units={units}
        label="Current Value"
        statusLabel={resolvedStatusLabel}
      />
    );

    if (numericValue === undefined
        && visualizationType !== 'data_card'
        && visualizationType !== 'line_chart'
        && visualizationType !== 'single_value_line'
        && visualizationType !== 'vertical_bar_ts'
        && visualizationType !== 'horizontal_bar_ts') {
      return fallbackCard;
    }

    // configRangeMax (from admin thresholds) takes priority over CareDaily metadata max.
    // Falls back to metadata max, then a data-driven default. Admin thresholds are
    // entered in the display scale, but metadata max is raw, so it must be scaled
    // to match an already-×100 value — otherwise a gauge divides 82 by 1 and pins
    // at 100% showing "82 of 1".
    const rawMetadataMax = metadata?.max_value;
    const scaledMetadataMax = (typeof rawMetadataMax === 'number' && displayAs0to100)
      ? rawMetadataMax * 100
      : rawMetadataMax;
    const resolvedMax = configRangeMax ?? scaledMetadataMax;

    const renderNumericVisualization = (chartValue: number) => {
      switch (visualizationType) {
        case 'horizontal_bar': {
          const hasSparkline = hasSeriesVariation(timeSeriesData);
          return (
            <div className="metric-card__chart-with-sparkline">
              <HorizontalBarChart
                value={chartValue}
                targetValue={targetValue}
                targetMin={targetMin}
                targetMax={targetMax}
                colorStatus={colorStatus}
                customColor={customColor}
                units={units}
                maxValue={resolvedMax ?? Math.max(chartValue * 1.5, 100)}
                statusLabel={resolvedStatusLabel}
              />
              {hasSparkline && (
                <TrendLineChart
                  data={timeSeriesData!}
                  targetValue={targetValue}
                  targetMin={targetMin}
                  targetMax={targetMax}
                  colorStatus={colorStatus}
                  customColor={customColor}
                  units={units}
                  maxValue={resolvedMax}
                  compact
                />
              )}
            </div>
          );
        }
        
        case 'vertical_bar': {
          const hasSparkline = hasSeriesVariation(timeSeriesData);
          return (
            <div className="metric-card__chart-with-sparkline">
              <VerticalBarChart
                value={chartValue}
                targetValue={targetValue}
                targetMin={targetMin}
                targetMax={targetMax}
                colorStatus={colorStatus}
                customColor={customColor}
                units={units}
                maxValue={resolvedMax ?? 100}
                statusLabel={resolvedStatusLabel}
              />
              {hasSparkline && (
                <TrendLineChart
                  data={timeSeriesData!}
                  targetValue={targetValue}
                  targetMin={targetMin}
                  targetMax={targetMax}
                  colorStatus={colorStatus}
                  customColor={customColor}
                  units={units}
                  maxValue={resolvedMax}
                  compact
                />
              )}
            </div>
          );
        }
        
        case 'circle_gauge':
          return (
            <CircleGauge
              value={chartValue}
              maxValue={resolvedMax ?? 100}
              targetValue={targetValue}
              targetMin={targetMin}
              targetMax={targetMax}
              colorStatus={colorStatus}
              customColor={customColor}
              units={units}
              statusLabel={resolvedStatusLabel}
            />
          );
        
        default:
          return null;
      }
    };
    
    const renderTimeSeriesBars = (orientation: 'vertical' | 'horizontal') => {
      const tsData = timeSeriesData || [];
      const tsTitle = 'Current';

      if (tsData.length <= 1) {
        if (numericValue === undefined) return fallbackCard;
        const BarComp = orientation === 'vertical' ? VerticalBarChart : HorizontalBarChart;
        return (
          <div className="metric-card__viz-wrapper">
            <div className="metric-card__chart-title">{tsTitle}</div>
            <BarComp
              value={numericValue}
              targetValue={targetValue}
              targetMin={targetMin}
              targetMax={targetMax}
              colorStatus={colorStatus}
              customColor={customColor}
              units={units}
              maxValue={resolvedMax ?? Math.max(numericValue * 1.5, (targetValue ?? 0) * 1.5, (targetMax ?? 0) * 1.5)}
              statusLabel={resolvedStatusLabel}
            />
          </div>
        );
      }

      const Component = orientation === 'vertical' ? VerticalTimeSeriesBar : HorizontalTimeSeriesBar;
      const latestVal = tsData[tsData.length - 1].value;
      const fmtVal = (v: number) => v % 1 === 0 ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

      return (
        <div className="metric-card__viz-wrapper">
          <div className="metric-card__chart-title">{tsTitle}</div>
          <div className="metric-card__current-value">
            <span className="metric-card__current-value-number">{fmtVal(latestVal)}</span>
            {units && <span className="metric-card__current-value-units">{units}</span>}
          </div>
          <Component
            data={tsData}
            targetValue={targetValue}
            targetMin={targetMin}
            targetMax={targetMax}
            colorStatus={colorStatus}
            customColor={customColor}
            units={units}
            maxValue={resolvedMax}
            thresholds={thresholds}
            statusLabel={resolvedStatusLabel}
            pointColorStatus={pointColorStatus}
          />
        </div>
      );
    };

    switch (visualizationType) {
      case 'horizontal_bar':
      case 'circle_gauge':
      case 'vertical_bar':
        return numericValue === undefined ? fallbackCard : renderNumericVisualization(numericValue);

      case 'vertical_bar_ts':
        return renderTimeSeriesBars('vertical');

      case 'horizontal_bar_ts':
        return renderTimeSeriesBars('horizontal');
      
      case 'line_chart':
      case 'single_value_line': {
        const chartData = timeSeriesData || [];
        const lineTitle = 'Current';

        // A line across identical readings looks like a trend where none exists, so a
        // flat series falls through to the same single-value view as a lone reading.
        if (!hasSeriesVariation(chartData) || visualizationType === 'single_value_line') {
          if (numericValue === undefined) return fallbackCard;
          return (
            <div className="metric-card__viz-wrapper">
              <div className="metric-card__chart-title">{lineTitle}</div>
              <SingleValueLineChart
                value={numericValue}
                targetValue={targetValue}
                targetMin={targetMin}
                targetMax={targetMax}
                colorStatus={colorStatus}
                customColor={customColor}
                units={units}
                maxValue={resolvedMax}
                statusLabel={resolvedStatusLabel}
              />
            </div>
          );
        }

        const latestValue = chartData[chartData.length - 1].value;
        const formatLatest = (v: number) => v % 1 === 0 ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });

        return (
          <div className="metric-card__viz-wrapper">
            <div className="metric-card__chart-title">{lineTitle}</div>
            <div className="metric-card__current-value">
              <span className="metric-card__current-value-number">{formatLatest(latestValue)}</span>
              {units && <span className="metric-card__current-value-units">{units}</span>}
            </div>
            <TrendLineChart
              data={chartData}
              targetValue={targetValue}
              targetMin={targetMin}
              targetMax={targetMax}
              colorStatus={colorStatus}
              customColor={customColor}
              units={units}
              maxValue={resolvedMax}
              statusLabel={resolvedStatusLabel}
            />
          </div>
        );
      }
      
      case 'data_card':
      default:
        return fallbackCard;
    }
  };

  const getTrendIndicator = () => {
    // Nothing moved: say so plainly when there are readings to compare, and show
    // nothing at all when there is only one reading (or none) — an arrow there would
    // be claiming a change the resident has no way to see.
    if (!hasSeriesVariation(timeSeriesData)) {
      return seriesIsFlat
        ? <span className="metric-card__trend metric-card__trend--flat">No change</span>
        : null;
    }

    if (typeof zscore !== 'number' || !isFinite(zscore)) return null;

    let trendIcon: string;
    let trendClass: string;
    let trendLabel: string;
    
    if (zscore > 0.5) {
      trendIcon = '↗';
      trendClass = 'trend--up';
      trendLabel = 'Trending up';
    } else if (zscore < -0.5) {
      trendIcon = '↘';
      trendClass = 'trend--down';
      trendLabel = 'Trending down';
    } else {
      return null;
    }
    
    return (
      <span
        className={`metric-card__trend ${trendClass}`}
        title={`${trendLabel} compared with your recent average`}
        aria-label={trendLabel}
      >
        {trendIcon}
      </span>
    );
  };

  const noteContent = (() => {
    const fromConfig = typeof tooltipText === 'string' ? tooltipText.trim() : '';
    const fromMeta = typeof metadata?.comment === 'string' ? String(metadata.comment).trim() : '';
    return fromConfig || fromMeta;
  })();

  return (
    <div
      className={`metric-card ${customColor ? 'metric-card--custom' : `metric-card--${colorStatus}`}`}
      style={customColor ? ({ '--metric-custom-color': customColor } as React.CSSProperties) : undefined}
    >
      <div className="metric-card__header">
        <div className="metric-card__title-row">
          {icon && isValidIcon(icon) && !isEmojiIcon(icon) && (
            <span className="metric-card__icon" aria-hidden="true">
              <Icon name={icon} size={18} />
            </span>
          )}
          <h3 className="metric-card__title">{title}</h3>
          {noteContent && !showInlineNote && (
            <Tooltip
              content={`Info: ${noteContent}`}
              position="top"
            >
              <button type="button" className="metric-card__info-icon info-dot-button" aria-label={`About ${title}`}>i</button>
            </Tooltip>
          )}
          {getTrendIndicator()}
          {(targetValue != null || targetMin != null || targetMax != null) && (
            <span className="metric-card__target-badge">
              Target: {targetMin != null && targetMax != null
                ? `${targetMin}–${targetMax}`
                : targetMin != null
                  ? `≥ ${targetMin}`
                  : targetMax != null
                    ? `≤ ${targetMax}`
                    : targetValue}
              {units ? ` ${units}` : ''}
            </span>
          )}
        </div>
      </div>
      
      <div className="metric-card__content">
        <ChunkBoundary onRetry={handleChartRetry} rethrowNonChunkErrors>
          <React.Suspense fallback={<div className="metric-card__chart-loading" aria-hidden="true" />}>
            {renderVisualization()}
          </React.Suspense>
        </ChunkBoundary>
      </div>

      {timeSeriesFallbackNotice && (
        <p className="metric-card__fallback-notice">{timeSeriesFallbackNotice}</p>
      )}

      {showInlineNote && noteContent && (
        <p className="metric-card__inline-note">{noteContent}</p>
      )}

      {normativeBasis && (
        <p className="metric-card__normative-basis">{normativeBasis}</p>
      )}
    </div>
  );
};
